// src/pages/TeamDetails.jsx
import { useState, useEffect, useMemo  } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiUsers, FiCalendar, FiPlus, FiCheckSquare } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import API from '../../services/api'; // adjust path as needed
import { getUserByEmail, addMemberToTeam, getTeamById, removeMemberFromTeam } from '../../services/action';

const TeamDetails = () => {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberError, setAddMemberError] = useState('');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    dueDate: ''

  });

 useEffect(() => {
  const fetchTeamDetails = async () => {
    try {
      // Change from /team to /teams
      const res = await API.get(`/team/${teamId}?includeTasks=true`);
      setTeam(res.data.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load team details');
      setLoading(false);
    }
  };
  fetchTeamDetails();
}, [teamId]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMemberEmail.trim()) return;

    setAddingMember(true);
    setAddMemberError('');

    try {
      const userRes = await getUserByEmail(newMemberEmail);
      if (!userRes.data.success) {
        throw new Error(userRes.data.message || 'User not found');
      }

      const userId = userRes.data.data._id;

      const addRes = await addMemberToTeam(teamId, userId);
      if (!addRes.data.success) {
        throw new Error(addRes.data.message || 'Failed to add member');
      }

      const updatedTeamRes = await getTeamById(teamId);
      setTeam(updatedTeamRes.data.data);
      setNewMemberEmail('');

    } catch (err) {
      setAddMemberError(err.message);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (memberUserId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      const res = await removeMemberFromTeam(teamId, memberUserId);
      if (!res.data.success) {
        throw new Error(res.data.message || 'Failed to remove member');
      }

      const updatedTeamRes = await getTeamById(teamId);
      setTeam(updatedTeamRes.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setTaskData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignTask = async (e) => {
    e.preventDefault();
    try {
      const res = await API.post(`tasks/${teamId}/tasks`, {
        ...taskData,
        createdBy: user._id
      });
      console.log('createdby:',user._id);
      
      if (res.data.success) {
        const updatedTeamRes = await getTeamById(teamId);
        setTeam(updatedTeamRes.data.data);
        setTaskData({
          title: '',
          description: '',
          assignedTo: '',
          dueDate: ''
        });
        setShowTaskModal(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign task');
    }
  };

  const memberDaysWorked = useMemo(() => {
    const daysMap = {};
    if (team && team.tasks) {
      team.tasks.forEach(task => {
        if (task.completed && task.assignedTo && task.dueDate) {
          const dueDate = new Date(task.dueDate);
          if (isNaN(dueDate.getTime())) return; // Skip invalid dates
          const memberId = task.assignedTo._id;
          const dueDateString = dueDate.toDateString();
          if (!daysMap[memberId]) {
            daysMap[memberId] = new Set();
          }
          daysMap[memberId].add(dueDateString);
        }
      });
      // Convert Set sizes to counts
      const result = {};
      Object.keys(daysMap).forEach(memberId => {
        result[memberId] = daysMap[memberId].size;
      });
      return result;
    }
    return {};
  }, [team]);
  

  const isAdmin = user?._id === team?.createdBy?._id;
  const isMember = team?.members?.some(member => member.user._id === user?._id);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error}</div>
        <Link to="/team" className="btn btn-primary mt-3">
          <FiArrowLeft className="me-2" />
          Back to Teams
        </Link>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning">Team not found</div>
        <Link to="/team" className="btn btn-primary mt-3">
          <FiArrowLeft className="me-2" />
          Back to Teams
        </Link>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="mb-4">
        <Link to={isAdmin ? "/team" : "/my-team"} className="btn btn-outline-secondary">
          <FiArrowLeft className="me-2" />
          {isAdmin ? "Back to Teams" : "Back to My Teams"}
        </Link>
      </div>

      <div className="card shadow">
        <div className="card-header bg-primary text-white">
          <h2 className="mb-0">{team.name}</h2>
        </div>

        <div className="card-body">
          <div className="row mb-4">
            <div className="col-md-6">
              <p className="text-muted">{team.description}</p>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-center gap-3 text-muted">
                <FiCalendar />
                <span>
                  Created: {new Date(team.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="d-flex align-items-center gap-3 text-muted mt-2">
                <FiUsers />
                <span>{team.members.length} members</span>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Members</h4>
            <div>
              {(isAdmin ) && (
                <button
                  className="btn btn-sm btn-info me-2"
                  onClick={() => setShowTaskModal(true)}
                >
                  <FiCheckSquare className="me-1" />
                  Assign Task
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn btn-sm btn-success"
                  data-bs-toggle="modal"
                  data-bs-target="#addMemberModal"
                >
                  <FiPlus className="me-1" />
                  Add Member
                </button>
              )}
            </div>
          </div>

           <div className="list-group mb-5">
        {team.members.map((member) => (
          <div
            key={member._id}
            className="list-group-item d-flex justify-content-between align-items-center"
          >
            <div>
              <span className="fw-bold">{member.user.name}</span>
              <span className="badge bg-info ms-2">{member.role}</span>
              {member.user._id === team.createdBy._id && (
                <span className="badge bg-primary ms-2">Admin</span>
              )}
              {/* Display days worked */}
              <span className="ms-2 text-muted">
                Days worked: {memberDaysWorked[member.user._id] || 0}
              </span>
            </div>
            {isAdmin && member.user._id !== team.createdBy._id && (
              <div className="btn-group">
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRemoveMember(member.user._id)}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

          {/* Tasks Section */}
          <h4 className="mb-3">Team Tasks</h4>
          {team.tasks && team.tasks.length > 0 ? (
            <div className="list-group">
              {team.tasks.map(task => (
                <div key={task._id} className="list-group-item">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h5>{task.title}</h5>
                      <p>{task.description}</p>
                      <small className="text-muted">
                        Assigned to: {task.assignedTo?.name || 'Unassigned'} | 
                        Due: {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No deadline'}
                      </small>
                    </div>
                    <div>
                      <span className={`badge ${task.completed ? 'bg-success' : 'bg-warning'}`}>
                        {task.completed ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info">No tasks assigned yet</div>
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      <div
        className="modal fade"
        id="addMemberModal"
        tabIndex="-1"
        aria-labelledby="addMemberModalLabel"
        aria-hidden="true"
        style={{ marginTop: "70px" }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleAddMember}>
              <div className="modal-header">
                <h5 className="modal-title" id="addMemberModalLabel">
                  Add New Member
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="memberEmail" className="form-label">
                    Member Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="memberEmail"
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    required
                  />
                </div>
                {addMemberError && (
                  <div className="alert alert-danger">{addMemberError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={addingMember}
                >
                  {addingMember ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Assign Task Modal */}
      <div className={`modal fade ${showTaskModal ? 'show d-block' : ''}`} tabIndex="-1" style={{ marginTop: "70px" }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleAssignTask}>
              <div className="modal-header">
                <h5 className="modal-title">Assign New Task</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTaskModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={taskData.title}
                    onChange={handleTaskInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={taskData.description}
                    onChange={handleTaskInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Assign To</label>
                  <select
                    className="form-select"
                    name="assignedTo"
                    value={taskData.assignedTo}
                    onChange={handleTaskInputChange}
                    required
                  >
                    <option value="">Select Member</option>
                    {team.members.map(member => (
                      <option key={member.user._id} value={member.user._id}>
                        {member.user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="dueDate"
                    value={taskData.dueDate}
                    onChange={handleTaskInputChange}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowTaskModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Assign Task
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {showTaskModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default TeamDetails;